import { Directive, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";

@Directive({
    selector: "[appLazyImageSrc]",
    standalone: true
})
export class LazyImageDirective implements OnInit, OnDestroy {
    @Input() appLazyImageSrc = "";
    @Input() lazyImageDelay = 0;

    private observer?: IntersectionObserver;
    private replayTimer?: ReturnType<typeof setTimeout>;

    constructor(private readonly el: ElementRef<HTMLImageElement>) {}

    ngOnInit(): void {
        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;
                    this.loadAndAddClass(this.lazyImageDelay);
                    this.observer?.disconnect();
                });
            },
            { threshold: 0.1 }
        );
        this.observer.observe(this.el.nativeElement);
    }

    /** Removes the visible state immediately — call on close so images start hidden on next open. */
    reset(): void {
        clearTimeout(this.replayTimer);
        this.el.nativeElement.classList.remove("loaded");
    }

    /** Adds the loaded class after `delay` ms. Only fires if src is already set. */
    addLoadedClass(delay = 0): void {
        const img = this.el.nativeElement;
        if (!img.src) return;
        clearTimeout(this.replayTimer);
        this.replayTimer = setTimeout(() => img.classList.add("loaded"), delay);
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
        clearTimeout(this.replayTimer);
    }

    private loadAndAddClass(delay: number): void {
        const img = this.el.nativeElement;
        setTimeout(() => {
            img.src = this.appLazyImageSrc;
            if (img.complete) {
                img.classList.add("loaded");
            } else {
                img.onload = () => img.classList.add("loaded");
            }
        }, delay);
    }
}
